import { registerTranslationRefresh, t as lnT } from '../../i18n';
import {useTranslation} from 'react-i18next';
import React from 'react';
import styled from 'styled-components';
import { format, isToday, isTomorrow } from 'date-fns';
import { FiPlus, FiTrash2, FiX } from 'react-icons/fi';
import { IoBedOutline, IoCalendarClearOutline, IoCalendarOutline, IoSunnyOutline } from 'react-icons/io5';

import Modal from './Modal';
import { GREEN } from '../../appearance/Colors';

const reminderPickerModalStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        width: 'min(92vw, 500px)',
        borderRadius: 12,
        border: 0,
        background: '#222729',
        padding: 0,
        overflow: 'hidden',
        boxShadow: '0 18px 50px rgba(0, 0, 0, 0.34)'
    }
};

const normalizeReminderDeliveryType = value => {
    const normalizedValue = String(value || '').trim().toLowerCase();
    return normalizedValue === 'email' ? 'EMAIL' : 'PUSH';
};

const makeTranslatedALERT_OFFSET_PRESETS = () => ([
    { offsetMinutes: 0, label: lnT('messages:atTimeOfEvent') },
    { offsetMinutes: -5, label: lnT('messages:5MinutesBefore') },
    { offsetMinutes: -10, label: lnT('messages:10MinutesBefore') },
    { offsetMinutes: -30, label: lnT('messages:30MinutesBefore') },
    { offsetMinutes: -60, label: lnT('messages:1HourBefore') },
    { offsetMinutes: -120, label: lnT('messages:2HoursBefore') },
    { offsetMinutes: -1440, label: lnT('messages:1DayBefore') },
    { offsetMinutes: -10080, label: lnT('messages:1WeekBefore') }
]);
let ALERT_OFFSET_PRESETS = makeTranslatedALERT_OFFSET_PRESETS();
registerTranslationRefresh(() => { ALERT_OFFSET_PRESETS = makeTranslatedALERT_OFFSET_PRESETS(); });

const MAX_ALERTS = 5;

let alertRowKeySeed = 0;
const nextAlertRowKey = () => {
    alertRowKeySeed += 1;
    return `alert-${Date.now()}-${alertRowKeySeed}`;
};

const buildDefaultAlertRows = initialDeliveryType => ([
    {
        key: nextAlertRowKey(),
        offsetMinutes: -60,
        deliveryType: normalizeReminderDeliveryType(initialDeliveryType)
    },
    {
        key: nextAlertRowKey(),
        offsetMinutes: 0,
        deliveryType: normalizeReminderDeliveryType(initialDeliveryType)
    }
]);

const buildAlertRowsFromMessageAlerts = messageAlerts => {
    const sortedAlerts = [...messageAlerts].sort((a, b) => a.offsetMinutes - b.offsetMinutes);
    return sortedAlerts.slice(0, MAX_ALERTS).map(alert => ({
        key: nextAlertRowKey(),
        offsetMinutes: alert.offsetMinutes,
        deliveryType: normalizeReminderDeliveryType(alert.deliveryType)
    }));
};

const getUnusedAlertPreset = usedOffsets => ALERT_OFFSET_PRESETS.find(
    preset => !usedOffsets.includes(preset.offsetMinutes)
);

const getRoundedFutureReminder = () => {
    const nextDate = new Date(Date.now() + 5 * 60 * 1000);
    nextDate.setSeconds(0, 0);
    const minutesRemainder = nextDate.getMinutes() % 5;
    if (minutesRemainder !== 0) {
        nextDate.setMinutes(nextDate.getMinutes() + (5 - minutesRemainder));
    }
    return nextDate;
};

const resolveFinalReminderDate = reminderAt => {
    if (!reminderAt) {
        return null;
    }

    const nextReminderAt = new Date(reminderAt);
    const now = new Date();
    while (nextReminderAt.getTime() <= now.getTime()) {
        nextReminderAt.setDate(nextReminderAt.getDate() + 1);
    }

    return nextReminderAt;
};

const getInitialReminderDate = (initialReminderAt, minimumSelectableDate) => {
    const parsedDate = initialReminderAt ? new Date(initialReminderAt) : null;
    if (parsedDate && !Number.isNaN(parsedDate.getTime())) {
        return parsedDate.getTime() < minimumSelectableDate.getTime() ? minimumSelectableDate : parsedDate;
    }
    return minimumSelectableDate;
};

const toDateInputValue = date => format(date, 'yyyy-MM-dd');
const toTimeInputValue = date => format(date, 'HH:mm');

const buildReminderDate = (dateValue, timeValue) => {
    if (!dateValue || !timeValue) {
        return null;
    }

    const [year, month, day] = dateValue.split('-').map(Number);
    const [hours, minutes] = timeValue.split(':').map(Number);

    if ([year, month, day, hours, minutes].some(value => !Number.isFinite(value))) {
        return null;
    }

    const nextDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
    if (Number.isNaN(nextDate.getTime())) {
        return null;
    }

    return nextDate;
};

const getDateLabel = dateValue => {
    if (!dateValue) {
        return lnT('common:today');
    }

    const parsedDate = new Date(`${dateValue}T00:00:00`);
    if (Number.isNaN(parsedDate.getTime())) {
        return lnT('common:today');
    }

    if (isToday(parsedDate)) {
        return lnT('common:today');
    }

    if (isTomorrow(parsedDate)) {
        return lnT('common:tomorrow');
    }

    return format(parsedDate, 'MMMM d, yyyy');
};

const getSubmitLabel = (dateValue, timeValue, isEditing) => {
    const timeLabel = timeValue || '--:--';
    const rawDateLabel = String(getDateLabel(dateValue) || 'Today');
    const dateLabel = rawDateLabel === 'Today' || rawDateLabel === 'Tomorrow'
        ? rawDateLabel.toLowerCase()
        : rawDateLabel;
    return lnT('common:at', { value1: String(isEditing ? 'Update' : 'Remind'), value2: String(dateLabel), value3: String(timeLabel) });
};

const formatReminderAt = value => {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) {
        return '';
    }

    if (isToday(date)) {
        return `Today at ${format(date, 'HH:mm')}`;
    }

    if (isTomorrow(date)) {
        return `Tomorrow at ${format(date, 'HH:mm')}`;
    }

    return format(date, "d MMM 'at' HH:mm");
};

const getComparableDateKey = date => format(date, 'yyyy-MM-dd');

const buildReminderPresetDate = (kind, now = new Date()) => {
    const base = new Date(now);
    base.setSeconds(0, 0);

    if (kind === 'today_evening') {
        base.setHours(20, 0, 0, 0);
        return base;
    }

    if (kind === 'tomorrow') {
        base.setDate(base.getDate() + 1);
        base.setHours(10, 0, 0, 0);
        return base;
    }

    if (kind === 'weekend') {
        const day = base.getDay();

        if (day === 6 || day === 0) {
            const weekendToday = new Date(base);
            weekendToday.setHours(10, 0, 0, 0);
            if (weekendToday.getTime() > now.getTime()) {
                return weekendToday;
            }

            if (day === 6) {
                const sundayMorning = new Date(base);
                sundayMorning.setDate(sundayMorning.getDate() + 1);
                sundayMorning.setHours(10, 0, 0, 0);
                return sundayMorning;
            }

            const nextSaturday = new Date(base);
            nextSaturday.setDate(nextSaturday.getDate() + 6);
            nextSaturday.setHours(10, 0, 0, 0);
            return nextSaturday;
        }

        const daysUntilSaturday = (6 - day + 7) % 7;
        base.setDate(base.getDate() + daysUntilSaturday);
        base.setHours(10, 0, 0, 0);
        return base;
    }

    if (kind === 'next_week') {
        const day = base.getDay();
        const daysUntilNextMonday = ((8 - day) % 7) || 7;
        base.setDate(base.getDate() + daysUntilNextMonday);
        base.setHours(10, 0, 0, 0);
        return base;
    }

    return base;
};

function ReminderPickerModal({
    isOpen,
    initialReminderAt,
    initialReminderFiredAt,
    initialDeliveryType,
    initialReminderMessage,
    initialAlerts,
    loading = false,
    onClose,
    onSubmit,
    onClearReminder
}) {
    const {t: lnT} = useTranslation();

    const dateInputRef = React.useRef(null);
    const [dateValue, setDateValue] = React.useState('');
    const [timeValue, setTimeValue] = React.useState('');
    const [alertRows, setAlertRows] = React.useState(() => buildDefaultAlertRows('PUSH'));
    const [reminderMessage, setReminderMessage] = React.useState('');
    const [validationError, setValidationError] = React.useState('');
    const reminderPresets = React.useMemo(
        () => ([
            { key: 'today_evening', label: lnT('common:today'), icon: IoCalendarOutline },
            { key: 'tomorrow', label: lnT('common:tomorrow'), icon: IoSunnyOutline },
            { key: 'weekend', label: lnT('messages:weekend'), icon: IoBedOutline },
            { key: 'next_week', label: lnT('messages:nextWeek'), icon: IoCalendarClearOutline }
        ]),
        [lnT, ]
    );
    const currentReminderLabel = React.useMemo(
        () => (initialReminderAt ? formatReminderAt(initialReminderAt) : ''),
        [lnT, initialReminderAt]
    );
    const currentReminderPrefix = React.useMemo(() => {
        if (!initialReminderAt) {
            return '';
        }

        return initialReminderFiredAt ? 'Reminded at' : 'Upcoming';
    }, [lnT, initialReminderAt, initialReminderFiredAt]);
    const minimumSelectableDate = React.useMemo(() => getRoundedFutureReminder(), [lnT, isOpen]);
    const minimumDateValue = React.useMemo(
        () => toDateInputValue(minimumSelectableDate),
        [lnT, minimumSelectableDate]
    );
    const selectedReminderAt = React.useMemo(
        () => buildReminderDate(dateValue, timeValue),
        [lnT, dateValue, timeValue]
    );
    const previewReminderAt = React.useMemo(
        () => resolveFinalReminderDate(selectedReminderAt),
        [lnT, selectedReminderAt]
    );
    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        const initialDate = getInitialReminderDate(initialReminderAt, minimumSelectableDate);
        setDateValue(toDateInputValue(initialDate));
        setTimeValue(toTimeInputValue(initialDate));
        setAlertRows(
            Array.isArray(initialAlerts) && initialAlerts.length > 0
                ? buildAlertRowsFromMessageAlerts(initialAlerts)
                : buildDefaultAlertRows(initialReminderAt ? initialDeliveryType : 'PUSH')
        );
        setReminderMessage(String(initialReminderMessage || ''));
        setValidationError('');
    }, [initialAlerts, initialDeliveryType, initialReminderAt, initialReminderMessage, isOpen, minimumSelectableDate]);

    const usedAlertOffsets = React.useMemo(
        () => alertRows.map(row => row.offsetMinutes),
        [lnT, alertRows]
    );

    const addAlertRow = React.useCallback(() => {
        setAlertRows(previousRows => {
            if (previousRows.length >= MAX_ALERTS) {
                return previousRows;
            }

            const usedOffsets = previousRows.map(row => row.offsetMinutes);
            const nextPreset = getUnusedAlertPreset(usedOffsets);
            if (!nextPreset) {
                return previousRows;
            }

            return [
                ...previousRows,
                { key: nextAlertRowKey(), offsetMinutes: nextPreset.offsetMinutes, deliveryType: 'PUSH' }
            ];
        });
    }, [lnT]);

    const removeAlertRow = React.useCallback(rowKey => {
        setAlertRows(previousRows => previousRows.filter(row => row.key !== rowKey));
    }, [lnT]);

    const updateAlertRowOffset = React.useCallback((rowKey, offsetMinutes) => {
        setAlertRows(previousRows => previousRows.map(row => (
            row.key === rowKey ? { ...row, offsetMinutes } : row
        )));
    }, [lnT]);

    const updateAlertRowDeliveryType = React.useCallback((rowKey, deliveryType) => {
        setAlertRows(previousRows => previousRows.map(row => (
            row.key === rowKey ? { ...row, deliveryType } : row
        )));
    }, [lnT]);

    const openDatePicker = React.useCallback(() => {
        const input = dateInputRef.current;
        if (!input) {
            return;
        }

        if (typeof input.showPicker === 'function') {
            input.showPicker();
            return;
        }

        input.focus();
        input.click();
    }, [lnT]);

    const applyPreset = React.useCallback(presetKey => {
        const presetDate = resolveFinalReminderDate(buildReminderPresetDate(presetKey));
        if (!presetDate) {
            return;
        }

        setDateValue(toDateInputValue(presetDate));
        setTimeValue(toTimeInputValue(presetDate));
        setValidationError('');
    }, [lnT]);

    const handleSubmit = async event => {
        event.preventDefault();

        const reminderAt = resolveFinalReminderDate(buildReminderDate(dateValue, timeValue));
        if (!reminderAt) {
            setValidationError(lnT('messages:chooseAValidDateAndTime'));
            return;
        }

        setValidationError('');
        await Promise.resolve(
            onSubmit?.({
                reminderAt,
                alerts: alertRows.map(row => ({
                    offsetMinutes: row.offsetMinutes,
                    deliveryType: row.deliveryType
                })),
                reminderMessage: String(reminderMessage || '').trim()
            })
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={() => {
                if (!loading) {
                    onClose?.();
                }
            }}
            style={reminderPickerModalStyles}
            contentLabel={lnT('messages:reminderPicker')}
        >
            <ReminderPickerShell>
                <ReminderPickerHeader>
                    <CloseButton
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        aria-label={lnT('messages:closeReminderPicker')}
                    >
                        <FiX size={22} />
                    </CloseButton>
                    <ReminderPickerTitleWrap>
                        <ReminderPickerTitle>{lnT('messages:scheduleReminder')}</ReminderPickerTitle>
                        {currentReminderLabel ? (
                            <ReminderPickerSubtitle $isFiredReminder={Boolean(initialReminderFiredAt)}>
                                {currentReminderPrefix}: {currentReminderLabel}
                            </ReminderPickerSubtitle>
                        ) : null}
                    </ReminderPickerTitleWrap>
                    <HeaderSpacer />
                </ReminderPickerHeader>

                <ReminderPickerForm onSubmit={handleSubmit}>
                    <PickerRow>
                        <DateField type="button" onClick={openDatePicker}>
                            <FieldDisplay>{getDateLabel(dateValue)}</FieldDisplay>
                            <InvisibleDateInput
                                ref={dateInputRef}
                                type="date"
                                value={dateValue}
                                min={minimumDateValue}
                                onChange={event => {
                                    setDateValue(event.target.value);
                                    setValidationError('');
                                }}
                            />
                        </DateField>
                        <AtLabel>{lnT('messages:at')}</AtLabel>
                        <TimeField
                            type="time"
                            step={300}
                            value={timeValue}
                            onChange={event => {
                                setTimeValue(event.target.value);
                                setValidationError('');
                            }}
                        />
                    </PickerRow>

                    <PresetRow>
                        {reminderPresets.map(preset => (
                            <PresetChip
                                key={preset.key}
                                type="button"
                                onClick={() => applyPreset(preset.key)}
                            >
                                <PresetIconWrap>
                                    <preset.icon size={16} />
                                </PresetIconWrap>
                                {preset.label}
                            </PresetChip>
                        ))}
                    </PresetRow>

                    <Section>
                        <AlertsSectionHeader>
                            <SectionLabel>{lnT('messages:alerts')}</SectionLabel>
                            {alertRows.length < MAX_ALERTS ? (
                                <AddAlertButton type="button" onClick={addAlertRow}>
                                    <FiPlus size={14} />{lnT('messages:addAlert')}</AddAlertButton>
                            ) : null}
                        </AlertsSectionHeader>

                        <AlertRowsList>
                            {alertRows.map(row => (
                                <AlertRow key={row.key}>
                                    <AlertPresetSelect
                                        value={row.offsetMinutes}
                                        onChange={event => updateAlertRowOffset(row.key, Number(event.target.value))}
                                    >
                                        {ALERT_OFFSET_PRESETS.map(preset => (
                                            <option
                                                key={preset.offsetMinutes}
                                                value={preset.offsetMinutes}
                                                disabled={
                                                    preset.offsetMinutes !== row.offsetMinutes
                                                    && usedAlertOffsets.includes(preset.offsetMinutes)
                                                }
                                            >
                                                {preset.label}
                                            </option>
                                        ))}
                                    </AlertPresetSelect>

                                    <DeliveryTypeGrid>
                                        <DeliveryTypeButton
                                            type="button"
                                            $active={row.deliveryType === 'PUSH'}
                                            onClick={() => updateAlertRowDeliveryType(row.key, 'PUSH')}
                                        >{lnT('messages:notification')}</DeliveryTypeButton>
                                        <DeliveryTypeButton
                                            type="button"
                                            $active={row.deliveryType === 'EMAIL'}
                                            onClick={() => updateAlertRowDeliveryType(row.key, 'EMAIL')}
                                        >{lnT('common:email')}</DeliveryTypeButton>
                                    </DeliveryTypeGrid>

                                    <RemoveAlertButton
                                        type="button"
                                        aria-label={lnT('messages:removeAlert')}
                                        onClick={() => removeAlertRow(row.key)}
                                    >
                                        <FiX size={16} />
                                    </RemoveAlertButton>
                                </AlertRow>
                            ))}
                            {alertRows.length === 0 ? (
                                <SectionHint>{lnT('messages:noAlertsThisReminderWillNotNotify')}</SectionHint>
                            ) : null}
                        </AlertRowsList>
                    </Section>

                    <Section>
                        <SectionLabel>{lnT('messages:customMessage')}</SectionLabel>
                        <ReminderMessageField
                            type="text"
                            value={reminderMessage}
                            maxLength={120}
                            onChange={event => setReminderMessage(event.target.value)}
                            placeholder={lnT('messages:reminder')}
                        />
                        <SectionHint>{lnT('messages:optionalThisReplacesTheDefaultReminderTitle')}</SectionHint>
                    </Section>

                    {validationError ? <ValidationMessage>{validationError}</ValidationMessage> : null}

                    <SubmitButton type="submit" disabled={loading}>
                        {loading
                            ? lnT('messages:savingReminder')
                            : getSubmitLabel(
                                previewReminderAt ? toDateInputValue(previewReminderAt) : dateValue,
                                previewReminderAt ? toTimeInputValue(previewReminderAt) : timeValue,
                                Boolean(initialReminderAt)
                            )}
                    </SubmitButton>
                    {initialReminderAt && onClearReminder ? (
                        <ClearReminderButton
                            type="button"
                            disabled={loading}
                            onClick={() => {
                                onClearReminder();
                            }}
                        >
                            <FiTrash2 size={14} />{lnT('messages:cancelReminder')}</ClearReminderButton>
                    ) : null}
                </ReminderPickerForm>
            </ReminderPickerShell>
        </Modal>
    );
}

const ReminderPickerShell = styled.div`
    color: rgba(255, 255, 255, 0.96);
    padding: 8px 12px 12px;
`;

const ReminderPickerHeader = styled.div`
    display: grid;
    grid-template-columns: 32px 1fr 32px;
    align-items: center;
    min-height: 40px;
    margin-bottom: 8px;
`;

const ReminderPickerTitleWrap = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
`;

const CloseButton = styled.button`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 0;
    background: transparent;
    color: rgba(255, 255, 255, 0.88);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.08);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ReminderPickerTitle = styled.div`
    margin: 0;
    text-align: center;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: rgba(255, 255, 255, 0.98);
    text-shadow: 0 1px 0 rgba(0, 0, 0, 0.35);
`;

const ReminderPickerSubtitle = styled.div`
    font-size: 13px;
    line-height: 1.35;
    color: ${props => (props.$isFiredReminder ? GREEN : 'rgba(255, 255, 255, 0.58)')};
    text-align: center;
`;

const HeaderSpacer = styled.div`
    width: 32px;
    height: 32px;
`;

const ReminderPickerForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const PickerRow = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1.2fr) auto minmax(220px, 0.9fr);
    align-items: center;
    gap: 8px;

    @media (max-width: 620px) {
        grid-template-columns: 1fr;
    }
`;

const DateField = styled.button`
    appearance: none;
    position: relative;
    min-height: 46px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.025);
    color: inherit;
    font: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0 18px;
    text-align: center;
`;

const FieldDisplay = styled.span`
    font-size: 16px;
    font-weight: 500;
    line-height: 1.2;
`;

const InvisibleDateInput = styled.input`
    position: absolute;
    inset: 0;
    opacity: 0;
    pointer-events: none;
`;

const AtLabel = styled.span`
    font-size: 16px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.86);
    text-align: center;

    @media (max-width: 620px) {
        display: none;
    }
`;

const TimeField = styled.input`
    min-height: 46px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.025);
    color: rgba(255, 255, 255, 0.96);
    font-size: 18px;
    font-weight: 600;
    letter-spacing: 0.06em;
    font-variant-numeric: tabular-nums;
    padding: 0 22px;
    text-align: center;

    &:focus {
        outline: none;
    }

    &::-webkit-calendar-picker-indicator {
        opacity: 0;
        position: absolute;
        right: 0;
        width: 0;
        margin: 0;
        pointer-events: none;
    }
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const SectionHint = styled.span`
    font-size: 12px;
    line-height: 1.45;
    color: rgba(255, 255, 255, 0.56);
`;

const PresetRow = styled.div`
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 2px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const PresetChip = styled.button`
    flex: 0 0 auto;
    min-height: 34px;
    border-radius: 9px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: transparent;
    color: rgba(255, 255, 255, 0.92);
    font-size: 13px;
    font-weight: 500;
    padding: 0 14px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &:hover {
        background: rgba(255, 255, 255, 0.08);
    }
`;

const PresetIconWrap = styled.span`
    display: inline-flex;
    align-items: center;
    color: #25b2ff;
    flex-shrink: 0;
`;

const SectionLabel = styled.span`
    font-size: 16px;
    color: rgba(255, 255, 255, 0.72);
`;

const DeliveryTypeGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
`;

const AlertsSectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const AddAlertButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: 0;
    background: transparent;
    color: #25b2ff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    padding: 4px 2px;

    &:hover {
        color: #4fc3ff;
    }
`;

const AlertRowsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const AlertRow = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr) auto;
    align-items: center;
    gap: 8px;

    @media (max-width: 620px) {
        grid-template-columns: 1fr auto;

        ${DeliveryTypeGrid} {
            grid-column: 1 / -1;
        }
    }
`;

const AlertPresetSelect = styled.select`
    min-height: 42px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.025);
    color: rgba(255, 255, 255, 0.95);
    font-size: 14px;
    font-weight: 500;
    padding: 0 10px;

    &:focus {
        outline: none;
        border-color: rgba(28, 159, 255, 0.42);
    }

    option {
        background: #222729;
        color: rgba(255, 255, 255, 0.95);
    }

    option:disabled {
        color: rgba(255, 255, 255, 0.3);
    }
`;

const RemoveAlertButton = styled.button`
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: transparent;
    color: rgba(255, 255, 255, 0.6);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
        background: rgba(255, 80, 80, 0.1);
        border-color: rgba(255, 100, 100, 0.4);
        color: rgba(255, 140, 140, 0.9);
    }
`;

const DeliveryTypeButton = styled.button`
    min-height: 42px;
    border-radius: 12px;
    border: 1px solid ${props => props.$active ? 'rgba(28, 159, 255, 0.58)' : 'rgba(255, 255, 255, 0.14)'};
    background: ${props => props.$active ? 'rgba(28, 159, 255, 0.16)' : 'rgba(255, 255, 255, 0.03)'};
    color: rgba(255, 255, 255, 0.95);
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
`;

const ReminderMessageField = styled.input`
    width: 100%;
    min-height: 42px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.025);
    color: rgba(255, 255, 255, 0.95);
    font-size: 15px;
    font-weight: 500;
    padding: 0 14px;

    &::placeholder {
        color: rgba(255, 255, 255, 0.34);
    }

    &:focus {
        outline: none;
        border-color: rgba(28, 159, 255, 0.42);
        background: rgba(255, 255, 255, 0.05);
    }
`;

const ValidationMessage = styled.div`
    color: rgba(255, 126, 126, 0.96);
    font-size: 16px;
`;

const SubmitButton = styled.button`
    min-height: 44px;
    border: none;
    border-radius: 12px;
    background: linear-gradient(180deg, #25b2ff 0%, #0895ea 100%);
    color: #ffffff;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    padding: 0 20px;

    &:disabled {
        opacity: 0.65;
        cursor: not-allowed;
    }
`;

const ClearReminderButton = styled.button`
    min-height: 38px;
    border: 1px solid rgba(255, 100, 100, 0.3);
    border-radius: 12px;
    background: transparent;
    color: rgba(255, 120, 120, 0.85);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;

    &:hover:not(:disabled) {
        background: rgba(255, 80, 80, 0.1);
        border-color: rgba(255, 100, 100, 0.5);
        color: rgba(255, 140, 140, 1);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

export default ReminderPickerModal;
